/**
 * Pure pattern/valueMap transform engine for custom column mappings.
 *
 * ReDoS safety: all pattern literal text is regex-escaped before compilation
 * and capture groups are non-greedy; the pattern string (PATTERN_MAX_LENGTH,
 * enforced at the schema boundary), matched cell values
 * (TRANSFORM_VALUE_MAX_LENGTH, enforced here), AND the number of `{N}`
 * placeholders (MAX_PATTERN_PLACEHOLDERS, enforced in compilePattern) are all
 * bounded so match time has a safe upper bound. The placeholder-count cap
 * exists because PATTERN_MAX_LENGTH alone still allows 100+ adjacent
 * placeholders like `{1}{2}{3}...{100}` (no literal separators required
 * between them) — chaining that many `(.+?)` capture groups triggers
 * catastrophic backtracking against a non-matching value (empirically,
 * ~30 groups take ~5s, ~35 groups take ~82s, growing exponentially), enough
 * to hang the single-threaded event loop since applyPattern runs per row on
 * both the API dry-run and worker import paths.
 *
 * No DB, R2, or Redis — pure logic only, shared by apps/api (dry-run) and
 * apps/worker (live import) so both apply identical transform behavior.
 */
import type { PatternTransform, ValueMapTransform } from '../schemas/import-transform.js';
import { TRANSFORM_VALUE_MAX_LENGTH } from '../schemas/import-transform.js';

const PLACEHOLDER_RE = /\{(\d+)\}/g;

/**
 * Hard cap on the number of `{N}` placeholders compilePattern will accept.
 * ReDoS guard: chaining more `(.+?)` capture groups than this triggers
 * catastrophic backtracking on a non-matching value (see file-level doc
 * comment above for the empirical blowup numbers).
 */
export const MAX_PATTERN_PLACEHOLDERS = 15;

/** Escapes regex metacharacters so operator-supplied literal text is safe to interpolate into a RegExp. */
export function escapeRegExp(literal: string): string {
  return literal.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

export interface CompiledPattern {
  regex: RegExp;
  /** Placeholder numbers in the order they appear in the pattern (1-based). */
  placeholderOrder: number[];
}

/**
 * Compiles a `{1} {2}` fill-in-the-blanks pattern into a ReDoS-safe RegExp.
 * Literal (non-placeholder) text is regex-escaped; capture groups use
 * non-greedy `(.+?)`. Returns null when the pattern has no placeholders, or
 * when it has more than MAX_PATTERN_PLACEHOLDERS placeholders (ReDoS guard —
 * see file-level doc comment).
 */
export function compilePattern(pattern: string): CompiledPattern | null {
  const placeholderOrder: number[] = [];
  let lastIndex = 0;
  let regexSource = '';
  PLACEHOLDER_RE.lastIndex = 0;
  let match: RegExpExecArray | null;
  while ((match = PLACEHOLDER_RE.exec(pattern)) !== null) {
    if (placeholderOrder.length >= MAX_PATTERN_PLACEHOLDERS) return null;
    regexSource += escapeRegExp(pattern.slice(lastIndex, match.index)) + '(.+?)';
    placeholderOrder.push(Number(match[1]));
    lastIndex = match.index + match[0].length;
  }
  if (placeholderOrder.length === 0) return null;
  regexSource += escapeRegExp(pattern.slice(lastIndex));
  return { regex: new RegExp(`^${regexSource}$`), placeholderOrder };
}

export interface PatternMatchResult {
  matched: boolean;
  /** Captured values keyed by placeholder number (1-based). Empty when matched=false. */
  valuesByPlaceholder: Map<number, string>;
}

/**
 * Applies a `pattern` transform to a single raw cell value.
 * Cell values longer than TRANSFORM_VALUE_MAX_LENGTH are treated as
 * non-matching (length-bound safety, see spec.md "Placeholder pattern
 * extraction").
 */
export function applyPattern(value: string, transform: PatternTransform): PatternMatchResult {
  const noMatch: PatternMatchResult = { matched: false, valuesByPlaceholder: new Map() };
  // Trim once up front, mirroring applyValueMap's trim — leading/trailing whitespace in a
  // real XLSX cell must not break a match against a pattern with literal text at its
  // start/end (e.g. 'powiat {1}').
  const trimmedValue = value.trim();
  if (trimmedValue.length > TRANSFORM_VALUE_MAX_LENGTH) return noMatch;
  const compiled = compilePattern(transform.pattern);
  if (!compiled) return noMatch;
  const match = compiled.regex.exec(trimmedValue);
  if (!match) return noMatch;
  const valuesByPlaceholder = new Map<number, string>();
  compiled.placeholderOrder.forEach((placeholderNumber, i) => {
    valuesByPlaceholder.set(placeholderNumber, (match[i + 1] ?? '').trim());
  });
  return { matched: true, valuesByPlaceholder };
}

/**
 * Applies a `valueMap` transform. Matching is case-insensitive and trimmed —
 * both dictionary keys and the incoming value are normalised the same way.
 * Returns matched=false when the value has no dictionary entry.
 */
export function applyValueMap(
  value: string,
  transform: ValueMapTransform,
): { matched: true; value: string } | { matched: false } {
  const normalized = value.trim().toLowerCase();
  for (const [key, mapped] of Object.entries(transform.map)) {
    if (key.trim().toLowerCase() === normalized) {
      return { matched: true, value: mapped };
    }
  }
  return { matched: false };
}
